/**
 * Activity Overview Widget - Shows activity summary on dashboard
 */

import { Link } from 'react-router-dom';
import { ArrowRight, Activity } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ActivityAreaChart } from '@/components/reports/charts/activity-area-chart';
import { useReportTimeline, useReportSummary, useReportTopActors } from '@/hooks/use-reports';

export function ActivityOverview() {
  const timelineQuery = useReportTimeline({ relativeDays: 7 });
  const summaryQuery = useReportSummary({ relativeDays: 7, groupBy: 'action' });
  const topActorsQuery = useReportTopActors({ relativeDays: 7, limit: 5 });

  const isLoading = timelineQuery.isLoading || summaryQuery.isLoading || topActorsQuery.isLoading;

  // Calculate total
  const currentTotal = summaryQuery.data?.total ?? 0;

  if (!isLoading && currentTotal === 0) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-base font-medium">Activity Overview</CardTitle>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/pac/reports" className="gap-1">
              View Reports <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Activity className="mb-2 h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No activity in the last 7 days</p>
            <p className="text-xs text-muted-foreground">
              Activity will appear here as you manage policies and attributes
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-base font-medium">Activity Overview</CardTitle>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/pac/reports" className="gap-1">
            View Reports <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {/* Last 7 Days with Sparkline */}
          <div>
            <p className="text-sm text-muted-foreground mb-1">Last 7 Days</p>
            {isLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <p className="text-2xl font-bold">{currentTotal.toLocaleString()}</p>
            )}
            <p className="text-xs text-muted-foreground mb-2">total events</p>
            {!isLoading && timelineQuery.data?.buckets && timelineQuery.data.buckets.length > 0 && (
              <ActivityAreaChart
                data={timelineQuery.data.buckets}
                className="h-20 [&_.recharts-cartesian-grid]:hidden [&_.recharts-xAxis]:hidden [&_.recharts-yAxis]:hidden"
              />
            )}
          </div>

          {/* By Action */}
          <div>
            <p className="text-sm text-muted-foreground mb-1">By Action</p>
            {isLoading ? (
              <Skeleton className="h-16 w-full" />
            ) : (
              <div className="space-y-1">
                {summaryQuery.data?.groups.slice(0, 3).map((group) => (
                  <div key={group.key} className="flex items-center justify-between text-sm">
                    <span className="truncate">{group.label}</span>
                    <span className="text-muted-foreground">
                      {currentTotal > 0
                        ? Math.round((group.count / currentTotal) * 100)
                        : 0}%
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Top Contributors */}
          <div>
            <p className="text-sm text-muted-foreground mb-1">Top Contributors</p>
            {isLoading ? (
              <Skeleton className="h-16 w-full" />
            ) : (
              <div className="space-y-1">
                {topActorsQuery.data?.actors.slice(0, 3).map((actor, i) => (
                  <div key={actor.id} className="text-sm truncate">
                    {i + 1}. {actor.name || actor.email} ({actor.count})
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
